import React, { useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Button, Row, Col } from "react-bootstrap";

import binoculers from "../assets/images/backgrounds/binoculers.jpg";
import aboutus from "../assets/images/backgrounds/aboutus.jpg";
import { Pages } from "../components/layout";
import useTitle from "../components/hooks/useTitle";
import useMetaDescription from "../components/hooks/useMetaDescription";
import { routes } from "../utils";
import { concepts } from "fearless-common";

export default function AboutUs() {
  useTitle("About Fearless League");
  useMetaDescription(
    "Fearless League gives life scientists one connected platform they never outgrow."
  );

  useEffect(() => {
    // fade-in
    const els = document.querySelectorAll(".fade-in");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => obs.observe(el));
  }, []);

  return (
    <Pages.Base>
      {/* container with bg image */}
      <div
        style={{
          minHeight: "60vh",
          background: `url(${binoculers}) center/cover no-repeat`,
          position: "relative",
          overflow: "hidden",
          display: "flex"
        }}
      >
        {/* overlay, make image darken */}
        <div className="hero-overlay" />

        <Container className="h-100 d-flex align-items-center justify-content-center text-center my-auto">
          <div className="glass fade-in">
            <h1 className="hero-title">We are Fearless League</h1>
            <p className="hero-sub">
              Empowering Scientists Through Collaborative Discovery.
            </p>
            <a href={routes.photo51}>
              <Button variant="primary" className="btn-lg shadow-sm mt-2">
                  {`Try ${concepts.Photo51} for free`}
              </Button>
            </a>
          </div>
        </Container>
      </div>

      <section className="section fade-in">
        <h2 className="gradient-text center">
          Fearless League gives life scientists one connected platform they
          never outgrow, from their first experiment <br></br> to their thousandth.
        </h2>
      </section>

      <section className="section fade-in">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <h3 className="fw-bold mb-4">About Fearless League</h3>
              <p className="body-text">
                Fearless League builds software for the life sciences, designed to support researchers from their earliest work in the classroom through to teams at global pharmaceutical and biotech companies.
              </p>
            </Col>
            <Col md={6}>
              <div
                style={{
                  background: `url(${aboutus}) center/cover no-repeat`
                }}  
                className="gradient-box"
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* CARDS */}
      <section className="section light fade-in">
        <Container>
          <Row className="g-4">
            {[
              "We work closely with scientists, educators, and students, not just as users, but as co-developers who shape what we build. Our software is designed for teams, with collaboration built into its DNA.",
              "Good science depends on more than ideas. It depends on structure, usability, and documentation that holds up over time. We're building a connected set of tools that support the full arc of scientific work, from how science is taught and learned, to how experiments are designed, documented, and carried forward.",
              "As a mission-driven organization, everything we earn goes back into better tools and the community around them. Part of that commitment means ensuring the next generation of scientists has access to the same kinds of tools used in industry, which is why we work to keep our software within reach for educational institutions too."
            ].map((t, i) => (
              <Col md={4} key={i}>
                <div className="card-3d">
                  <p>{t}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* later we can add people avatars here */}
      {/* <section className="section fade-in">
        <div className="timeline">
          {[
            ["Learn", "From their earliest work in the classroom."],
            ["Build", "Experiments are designed, documented, and improved."],
            ["Scale", "Supporting teams at global pharmaceutical and biotech companies."]
          ].map(([title, text], i) => (
            <div key={i} className="timeline-item">
              <h5>{title}</h5>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section> */}

      <section className="section dark fade-in">
        <h2 className="center white">
          Science should be accessible.
          <br />
          So should the tools that support it.
        </h2>
      </section>

      {/* STYLES */}
      <style>{`
        .section { padding: 120px 20px; }
        .light { background:#f8fafc; }
        .dark { background: linear-gradient(135deg,#0f172a,#1e293b); }
        .center { text-align:center; max-width:900px; margin:0 auto; }
        .white { color:white; }

        .hero-overlay {
          position:absolute; inset:0;
          background: linear-gradient(135deg, rgba(0,0,0,0.7), rgba(30,41,59,0.8));
        }

        .glass {
          padding:60px;
          border-radius:24px;
          backdrop-filter: blur(15px);
          background: rgba(255,255,255,0.05);
          border:1px solid rgba(255,255,255,0.1);
        }

        .hero-title {
          font-size:3.5rem;
          font-weight:700;
          background: linear-gradient(90deg,#fff,#93c5fd);
          -webkit-background-clip:text;
          -webkit-text-fill-color:transparent;
        }

        .hero-sub {
          color:#cbd5f5;
          font-size:1.2rem;
          margin-top:20px;
        }

        .gradient-text {
          margin-top: 12px;
          font-size:2.6rem;
          font-weight:700;
          background: linear-gradient(90deg,#111,#4f46e5);
          -webkit-background-clip:text;
          -webkit-text-fill-color:transparent;
        }

        .body-text { color:#555; line-height:1.9; }

        .gradient-box {
          margin-top: 12px;
          height:280px;
          border-radius:24px;
          box-shadow:0 30px 80px rgba(99,102,241,0.5);
        }

        .card-3d {
          padding:30px;
          border-radius:20px;
          background: #eeeeee;
          transition:all .3s ease;
          box-shadow:0 10px 30px rgba(0,0,0,0.05);
          min-height: 313px;
        }

        .card-3d:hover {
          transform:translateY(-12px) rotateX(3deg);
          box-shadow:0 30px 60px rgba(0,0,0,0.12);
          background: #ececec;
        }

        .timeline {
          max-width:700px;
          margin:0 auto;
          border-left:2px solid #ddd;
          padding-left:30px;
        }

        .timeline-item { margin-bottom:40px; }

        .fade-in {
          opacity:0;
          transform:translateY(40px);
          transition:all .8s ease;
        }

        .fade-in.visible {
          opacity:1;
          transform:none;
        }

        @keyframes move {
          from { transform: translateY(0); }
          to { transform: translateY(-40px); }
        }
      `}</style>
    </Pages.Base>
  );
}